/**
 * English alongside Arabic. Same message ids, professional register — this is an
 * official, government-facing product in Jordan, so the copy is written as English
 * institutional prose, not a word-for-word rendering of the Arabic.
 * Any id missing here falls back to the Arabic source (see resolve() in ./messages).
 */
import type { MessageId } from './ar';

export const en: Partial<Record<MessageId, string>> = {
  /* ── general ─────────────────────────────────────────────────────── */
  'app.title': 'Rafrafa Platform',
  'app.brandAria': 'Rafrafa Platform — home',
  'common.login': 'Sign in',
  'common.home': 'Home',
  'common.signOut': 'Sign out',
  'common.signOutLong': 'Sign out',
  'common.openMenu': 'Open menu',
  'password.show': 'Show password',
  'password.hide': 'Hide password',
  'password.capsLock': 'Caps Lock is on — letters are typed in uppercase.',
  'common.closeMenu': 'Close menu',
  'common.mainNav': 'Main navigation',
  'common.loadingSession': 'Verifying your session…',
  'common.loadingScreen': 'Loading…',
  'common.available': 'Available',
  'common.unavailable': 'Not available',

  /* ── kid shell ───────────────────────────────────────────────────── */
  'brand.name': 'Rafrafa',
  'kid.rafif.name': 'Rafif',
  'kid.rafif.bubble': "I'm Rafif, I'm here to help",
  'kid.nav.home': 'My page',
  'kid.nav.class': 'Online class',
  'kid.nav.school': 'My school',
  'kid.nav.homework': 'My homework',
  'kid.nav.inbox': 'Inbox',
  'kid.nav.grades': 'My grades',
  'kid.nav.games': 'My exercises',
  'kid.nav.reports': 'My reports',
  'kid.nav.recommendations': 'Counsellor tips',
  'kid.nav.aria': 'My page sections',
  'kid.unread': '{count} unread',
  'kid.intro.home': 'This is your page — everything of yours in one place.',
  'kid.intro.class': 'Your online lessons: what is on now and what comes next.',
  'kid.intro.school': 'Your school and your teachers — you can message them from here.',
  'kid.intro.homework': 'Your homework: new, sent, and what your teacher reviewed.',
  'kid.intro.grades': 'These are your grades in every subject once your school publishes them.',
  'kid.intro.inbox': 'Messages and news that reached you.',
  'kid.intro.games': 'Pick a short exercise — you can stop whenever you like.',

  /* ── theme and language switchers ─────────────────────────────────── */
  'theme.label': 'Appearance',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.switchTo': 'Change appearance — currently {current}, next {next}',
  'lang.label': 'Language',
  'lang.ar': 'العربية',
  'lang.en': 'English',
  'lang.switchTo': 'Switch language to {next}',

  /* ── roles and tracks ─────────────────────────────────────────────── */
  'role.PLATFORM_ADMIN': 'Platform administration',
  'role.PLATFORM_SUPPORT': 'Beneficiary services department',
  'role.PLATFORM_ACADEMIES': 'Academies and courses department',
  'role.PLATFORM_INSTITUTIONS': 'Centers and schools department',
  'role.PLATFORM_FINANCE': 'Finance department',
  'role.INSTITUTION_ADMIN': 'Institution administration',
  'role.INTERNAL_SUPERVISOR': 'Internal supervisor',
  'role.SPECIALIST': 'Specialist',
  'role.COUNSELOR': 'School counsellor',
  'role.TEACHER': 'Teacher',
  'role.PARENT': 'Guardian',
  'role.STUDENT': 'Student',
  'role.PROFESSIONAL': 'Professional account',
  'role.REGULATORY_SUPERVISOR': 'Regulatory supervisor',
  'role.CLIENT': 'Client',
  'role.OFFICE_STAFF': 'Office staff',
  'role.INSTITUTION_DELEGATE': 'Delegate of the administration',
  'role.GRANT_ATTENDANCE': 'Attendance permission',
  'role.GRANT_CLASS_RECORDING': 'Class recording permission',
  'track.SPECIAL_EDUCATION': 'Special education track',
  'track.COUNSELING': 'Counselling track',
  'institutionType.CENTER': 'Centre',
  'institutionType.SCHOOL': 'School',

  /* ── app shell ────────────────────────────────────────────────────── */
  'shell.noRole': 'No active role',
  'shell.switchInstitution': 'Switch institution',
  'shell.platformScopeOption': '— Platform scope (no institution) —',
  'shell.platformScope': 'Platform scope',
  'shell.regulatorScope': 'Regulatory authority account',
  'shell.effectiveRole': 'Active role:',

  /* ── navigation ───────────────────────────────────────────────────── */
  'nav.group.oversight': 'Regulatory authority',
  'nav.group.platform.institutions': "Platform — Institutions",
  'nav.group.platform.accounts': "Platform — Accounts",
  'nav.group.platform.money': "Platform — Money & complaints",
  'nav.group.platform.content': "Platform — Content",
  'nav.group.platform.audit': "Platform — Follow-up and audit",
  'nav.group.platform': 'Platform',
  'nav.group.institution': 'Institution',
  'nav.group.me': 'My account',
  'nav.oversight.linked': 'Oversight',
  'nav.platform.institutions': 'Institutions',
  'nav.platform.regulatory': 'Regulatory bodies',
  'nav.platform.regulators': 'Regulatory accounts',
  'nav.platform.consentTexts': 'Consent texts',
  'nav.platform.seatRequests': 'Seat increase requests',
  'nav.platform.audit': 'Platform audit log',
  'nav.platform.notes': 'Improvement notes',
  'nav.platform.myAudit': 'My activity log',
  'nav.app.dashboard': 'Home',
  'nav.app.review': 'Review centre',
  'nav.app.students': 'Students',
  'nav.app.linkRequests': 'Link requests',
  'nav.app.plans': 'Plans and goals',
  'nav.app.preparations': 'Daily preparation',
  'nav.app.sessions': 'Sessions',
  'nav.app.reports': 'Reports',
  'nav.app.caseload': 'Students assigned to you',
  'nav.app.caseloadAdmin': 'Assign students',
  'nav.app.counseling': 'Counselling file',
  'nav.app.referrals': 'Referral requests',
  'nav.app.users': 'Accounts and memberships',
  'nav.app.orgChart': 'Organization chart',
  'nav.app.holidays': 'Holidays',
  'nav.app.leave': 'Leave requests',
  'nav.app.homeSession': 'Record a session for parents',
  'nav.app.myAttendance': 'My attendance',
  'nav.app.staffAttendance': 'Staff attendance',
  'nav.app.complaints': 'Complaints box',
  'nav.app.approvals': 'Approval chains',
  'nav.platform.holidays': 'Official holidays',
  'nav.app.invitations': 'Invitations',
  'nav.app.credentials': 'Staff credentials',
  'nav.app.settings': 'Institution settings',
  'nav.app.branding': 'Identity card',
  'nav.app.alerts': 'Alerts',
  'nav.app.indicators': 'Indicators',
  'nav.app.rules': 'Rules',
  'nav.app.excellence': 'Professional excellence',
  'nav.app.broadcast': 'Class recordings',
  'nav.app.regulatoryScope': 'What you share with regulators',
  'nav.app.integration': 'What leaves your institution',
  'nav.app.billing': 'Subscription and billing',
  'nav.app.jobs': 'Recruitment and directory',
  'nav.app.continuity': 'Continuity package',
  'nav.app.audit': 'Audit log',
  'nav.me.assignments': "Homework",
  'nav.me.class': 'My students',
  'nav.me.rosterPlans': 'My students’ plans',
  'nav.me.referrals': 'Referral requests for students',
  'nav.me.timetable': 'My timetable',
  'nav.app.classrooms': 'Classes and sections',
  'nav.app.academics': 'Academic setup',
  'nav.app.worksheets': 'Worksheets',
  'kid.nav.timetable': 'My timetable',
  'nav.me.home': 'Home',
  'nav.me.drafts': 'Drafts',
  'nav.me.children': 'My children',
  'nav.me.reports': "My children's reports",
  'nav.me.reports.student': 'My reports',
  'nav.me.recommendations': 'Counsellor recommendations',
  'nav.me.consents': 'Consents and signatures',
  'nav.me.excellence': 'My professional score',
  'nav.me.classBroadcasts': "My child's recorded classes",
  'nav.me.jobs': 'Vacancies and my certificates',
  'nav.me.continuity': "My children's packages",
  'nav.me.professional': 'My professional profile',
  'nav.me.invitations': 'Membership invitations',

  /* ── public layout ────────────────────────────────────────────────── */
  'public.nav.aria': 'Public pages',
  'public.nav.directory': 'Public directories',
  'centers.title': 'Institutions',
  'centers.sub': 'Active organisations that chose to appear on the platform. Pick your city or search by name, and open any card for what it has published.',
  'centers.join': 'Join your centre or school to Rafrafa',
  'centers.search': 'Search by name',
  'centers.search.hint': 'The search runs on the list already shown — what you type is sent to no one.',
  'centers.city': 'City',
  'centers.city.all': 'All cities',
  'centers.city.hint': '{n} cities have listed organisations.',
  'centers.city.empty': 'No cities yet — they appear once the first organisation is listed.',
  'centers.tab.centers': 'Centres',
  'centers.tab.schools': 'Schools',
  'centers.tabs.aria': 'Organisation type',
  'centers.loading': 'Loading centres and schools…',
  'centers.empty.center': 'No centres listed yet',
  'centers.empty.school': 'No schools listed yet',
  'centers.none.center': 'No centre matches your search',
  'centers.none.school': 'No school matches your search',
  'centers.empty.text': 'Appearing here is each organisation own choice — once chosen, it shows on this page at once.',
  'centers.none.text': 'Try a shorter name, or All cities, or look in the other tab.',
  'centers.clear': 'Clear filters',
  'centers.map': 'Location on Google Maps',
  'centers.alsoSchool': 'and a school',
  'centers.privacy': 'No email, phone or ID number appears on this page, and nothing about an organisation appears unless it chose to publish it. Contact goes through the platform: an invitation its owner accepts.',
  'centers.logoAlt': '{name} logo',
  'institutionType.CENTER.short': 'Centre',
  'institutionType.SCHOOL.short': 'School',
  'public.nav.verify': 'Verify',
  'public.nav.news': 'News',
  'public.nav.open': 'Open section menu',
  'public.nav.close': 'Close section menu',
  'public.footer.rights': '© {year} Rafrafa Platform',

  /* ── landing page ─────────────────────────────────────────────────── */
  'landing.nav.aria': 'Page sections',
  'landing.nav.who': 'Who it serves',
  'landing.nav.journey': 'How it works',
  'landing.nav.news': 'News',
  'landing.nav.public': 'Public directories',
  'landing.cta.professional': 'Professional account',
  'landing.hero.kicker': 'The integrated Arabic platform for running education, special education and counselling',
  'landing.hero.slogan': 'Every flutter carries the possibility of change',
  'landing.hero.tagline': 'Everyone offers services; Rafrafa offers solutions',
  'landing.cta.guardian': 'Guardian account',
  'landing.hero.guardianNote': 'Guardians start on their own: create your account, add your children, then ask their institutions to link them.',
  'landing.aud.open': 'See the details',
  'landing.aud.aria': 'Why Rafrafa — for each side',
  'landing.aud.inst.t': 'Why Rafrafa for centres?',
  'landing.aud.inst.1.t': 'One place to run the institution',
  'landing.aud.inst.1.d': 'Your daily operating structure in one space — student files, plans and reports, the professional record of your specialists, and the recurring tasks and routines, so work stays organised and easy to follow.',
  'landing.aud.inst.2.t': 'Live oversight of the work',
  'landing.aud.inst.2.d': 'Clearer visibility into how a service is delivered and followed up — recorded documentation, live streaming when needed, and documented individual plans, so progress and quality can be tracked.',
  'landing.aud.inst.3.t': 'A professional presence',
  'landing.aud.inst.3.d': 'A digital presence that reflects your identity and experience — a full profile page showing your services, specialities, team and achievements, and making it easy for families to reach you.',
  'landing.aud.inst.4.t': 'A digital front for your institution',
  'landing.aud.inst.4.d': 'A complete page that connects the institution to families — an introduction to the institution, its services and its team, with a dedicated door for parents to register and find the services their children need.',
  'landing.aud.inst.5.t': 'An open door to talent and opportunity',
  'landing.aud.inst.5.d': 'A way to attract talent and advertise openings — reaching a specialised network of practitioners in special education and counselling, and searching by speciality and experience.',
  'landing.aud.inst.6.t': 'A standing partnership with families',
  'landing.aud.inst.6.d': 'A direct channel between the institution and the family through the student journey — sharing recommendations and daily updates, messaging parents, and keeping them informed of their children\u2019s progress, plans and needs.',
  'landing.aud.school.t': 'Why Rafrafa for schools?',
  'landing.aud.school.1.t': 'One place to run the school',
  'landing.aud.school.1.d': 'Your daily operating structure in one space — student files, plans and reports, team management, tasks and recurring routines, so work stays organised and easy to follow.',
  'landing.aud.school.2.t': 'A cumulative file for every student',
  'landing.aud.school.2.d': 'A counselling and behaviour file that follows each student through their school years, and a special-education file for those who need those services — with the information able to move, by the guardian\u2019s consent, when a student changes school.',
  'landing.aud.school.3.t': 'One referral system',
  'landing.aud.school.3.d': 'A teacher can raise a referral when they notice a need worth following up, route it to the right practitioner, and have the reason, the path and the actions taken all recorded.',
  'landing.aud.school.4.t': 'A system for support education',
  'landing.aud.school.4.d': 'A complete way to run support-education services inside the school: individual plans and session scheduling, documentation of delivery in writing or on video, notes and recommendations, reports, and tracking of the student\u2019s progress.',
  'landing.aud.school.5.t': 'Live oversight of the work',
  'landing.aud.school.5.d': 'Clearer visibility of how services are delivered — recorded documentation, live streaming when needed, and documented individual plans that support following delivery and quality.',
  'landing.aud.school.6.t': 'Private professional spaces',
  'landing.aud.school.6.d': 'A space of their own for the counsellor and the special-education specialist to record notes and recommendations and follow cases, deciding what is shared with the guardian and what stays in the professional record.',
  'landing.aud.school.7.t': 'A direct line to the family',
  'landing.aud.school.7.d': 'An organised channel connecting the school to parents: sharing recommendations and updates, following the student\u2019s needs and progress, and talking directly to the administration and practitioners.',
  'landing.aud.school.8.t': 'Counselling and support sessions',
  'landing.aud.school.8.d': 'Sessions for the student or the guardian can be booked online or in person, according to the services the school offers.',
  'landing.aud.school.9.t': 'An open door to talent and opportunity',
  'landing.aud.school.9.d': 'A way for the school to attract talent and advertise openings, reaching practitioners in special education, counselling and teaching by speciality and experience.',
  'landing.aud.school.10.t': 'Complete tracking and reports',
  'landing.aud.school.10.d': 'Boards and reports that help the school follow counselling, special education and support education, and review how plans, services and student needs are being met.',
  'landing.aud.school.11.t': 'A full gateway for families',
  'landing.aud.school.11.d': 'A digital space where families get to know the school and its services, complete registration, and follow what concerns their children and talk to the school from their own accounts.',
  'landing.aud.parent.t': 'Why Rafrafa for parents?',
  'landing.aud.parent.lead': 'Everything about your child in one place.',
  'landing.aud.parent.1': 'Rafrafa gives you one cumulative file that carries your child\u2019s reports, plans and assessments through every stage — and you control who may see it, and share it with more than one institution or practitioner when needed.',
  'landing.aud.parent.2': 'You talk directly to the institution your child is linked to, and follow progress as it happens through plain reports and clear recommendations that help you understand what is happening and continue it at home.',
  'landing.aud.parent.3': 'Rafrafa also gives you a documented directory of institutions, specialists and consultants, and lets you book counselling sessions for yourself or your child — online or in person — straight from your account.',
  'landing.aud.parent.4': 'And you will find courses and awareness resources made for parents, to help you understand your child\u2019s journey and take steps with more confidence.',
  'landing.aud.pro.t': 'Why Rafrafa for specialists, consultants and trainers?',
  'landing.aud.pro.1.t': 'A cumulative professional file',
  'landing.aud.pro.1.d': 'A professional page holding your speciality, experience, services and achievements, so whoever is looking for your expertise can find you. And your file travels with you: experience, session hours, achievements and credentials in one professional record.',
  'landing.aud.pro.2.t': 'Documented hours and experience',
  'landing.aud.pro.2.d': 'Session and work hours are counted inside the platform, so your professional experience stays documented and can be referred back to.',
  'landing.aud.pro.3.t': 'Certificate of excellence',
  'landing.aud.pro.3.d': 'Your achievements make your path — the certificate of excellence is granted against documented achievement and performance criteria inside the platform.',
  'landing.aud.pro.4.t': 'Job opportunities',
  'landing.aud.pro.4.d': 'Look for openings that match your speciality and experience among those advertised by centres, schools and institutions through a professional gateway — or let institutions find you.',
  'landing.aud.pro.5.t': 'An academy of your own, for trainers',
  'landing.aud.pro.5.d': 'For trainers accredited by the Accreditation and Quality Assurance Commission: a dedicated space to offer courses and attract trainees.',
  'landing.aud.pro.6.t': 'Sessions and consultations',
  'landing.aud.pro.6.d': 'Offer your sessions and consultations online, and set your times and prices from your account.',
  'landing.aud.pro.7.t': 'Professional tools',
  'landing.aud.pro.7.d': 'Tools that help with assessment, planning, report writing and organising your work.',
  'landing.who.kicker': 'Who it serves',
  'landing.who.title': 'Five parties, each with its own door',
  'landing.who.lead': 'One platform and one rule set — what differs is what each party sees and what it is allowed to do.',
  'landing.door.centers.t': 'Special education and counselling centres',
  'landing.door.centers.d': 'Both tracks end to end: assessment, plan, goals, preparation, recorded sessions, reports for three audiences, and quality and safeguarding indicators.',
  'landing.door.centers.cta': 'Institution sign-in',
  'landing.door.schools.t': 'Schools',
  'landing.door.schools.d': 'A counselling file for every student, not only those who struggle; a class roster for the teacher; documented referrals; and classroom broadcast to the guardians of that class, with each child’s family consent.',
  'landing.door.schools.cta': 'School sign-in',
  'landing.door.pros.t': 'Specialists and counsellors',
  'landing.door.pros.d': 'A professional account that belongs to you, not to the institution: it travels with you together with your credentials, your record and your score, and you accept or decline institutional invitations.',
  'landing.door.pros.cta': 'Create your professional account',
  'landing.door.families.t': 'Families',
  'landing.door.families.d': 'An account from which the guardian signs consents personally, reads what has been published for their child and watches the class broadcast — without access to the practitioner’s internal notes.',
  'landing.door.families.cta': 'Received an activation link?',
  'landing.door.regulators.t': 'Regulatory bodies',
  'landing.door.regulators.d': 'A compliance dashboard for the institutions linked to the body: counts, rates and scores — with no access to any individual record of a child or a member of staff.',
  'landing.door.regulators.cta': 'Regulatory supervisor sign-in',
  'landing.journey.kicker': 'How it works',
  'landing.journey.title': 'The service journey in ten stages',
  'landing.journey.lead': 'These stages are not a description: an automated test walks all of them before every release and checks the audit log against the order in which they happened. If it fails, the service does not ship.',
  'landing.step.1.t': 'The institution',
  'landing.step.1.d': 'Created and linked automatically to its mandatory supervising authority',
  'landing.step.2.t': 'The staff',
  'landing.step.2.d': 'Internal supervisors per track, teachers, and practitioners by invitation',
  'landing.step.3.t': 'The student and family',
  'landing.step.3.d': 'Enrolment with duplicate checking, a guardian invitation, and their own signature',
  'landing.step.4.t': 'Plan and goals',
  'landing.step.4.d': 'The practitioner writes, the supervisor reviews, administration approves',
  'landing.step.5.t': 'Preparation and session',
  'landing.step.5.d': 'No session without preparation; recording starts and ends with it',
  'landing.step.6.t': 'The report',
  'landing.step.6.d': 'One text for the institution, one for the family, one for the student',
  'landing.step.7.t': 'Review and approval',
  'landing.step.7.d': 'Whoever wrote it does not review it, and whoever reviewed it does not approve it',
  'landing.step.8.t': 'Referral',
  'landing.step.8.d': 'Documentation before the decision, and counselling referrals are accepted by default',
  'landing.step.9.t': 'Rules and indicators',
  'landing.step.9.d': 'A nightly pass sweeps, alerts, and computes quality and safeguarding',
  'landing.step.10.t': 'Oversight',
  'landing.step.10.d': 'The authority sees compliance aggregates, and every view is logged',
  'landing.rules.kicker': 'Rules that need no configuration',
  'landing.rules.title': 'Built into the server and the database, not into the screen',
  'landing.rules.lead': 'So no interface bug, no user edit and no direct API call can get around them.',
  'landing.rule.1.t': 'No session without preparation and a valid consent',
  'landing.rule.1.d': 'The server refuses to start the session — not the button.',
  'landing.rule.2.t': 'Four eyes',
  'landing.rule.2.d': 'A first review then a final approval, and no one reviews what they wrote.',
  'landing.rule.3.t': 'National ID numbers are never shown',
  'landing.rule.3.d': 'Encrypted at rest, returned masked, and every read of one is logged.',
  'landing.rule.4.t': 'Recordings are deleted when their term ends',
  'landing.rule.4.d': 'Automatically, every night — and if a deletion fails a critical alert is raised, not silence.',
  'landing.rule.5.t': 'Internal notes never reach the family',
  'landing.rule.5.d': 'Three texts for three audiences, separated by the server and not by the screen.',
  'landing.rule.6.t': 'No external channel without a contract and keys',
  'landing.rule.6.d': 'No image, no message and no payment leaves the platform before that.',
  'landing.public.kicker': 'No sign-in required',
  'landing.public.title': 'What you can do right now',
  'landing.public.directory.t': 'Directory of institutions and practitioners',
  'landing.public.directory.d': 'Those who chose to be listed publicly — institutions and specialists with their fields and cities.',
  'landing.public.verify.t': 'Verify a performance certificate',
  'landing.public.verify.d': 'Enter the certificate code to see its band, its period and its validity — the band, not the score.',
  'landing.public.register.t': 'New professional account',
  'landing.public.register.d': 'For specialists and counsellors; owned by its holder and travelling with them between institutions.',
  'landing.footer.logoAlt': 'Rafrafa logo',
  'landing.footer.about': 'Rafrafa — an Arabic platform dedicated to special education, learning and counselling, with high professional standards and strict protection of children\'s data.',
  'landing.footer.signInCol': 'Sign in',
  'landing.footer.guardianActivate': 'Received an invitation link? Activate your account',
  'landing.footer.guardianRegister': 'Create a guardian account',
  'landing.footer.privacy': 'Personal data is processed under Jordanian Personal Data Protection Law No. 24 of 2023',

  /* ── auth shell ───────────────────────────────────────────────────── */
  'auth.headline': 'Every flutter carries the possibility of change',
  'auth.tagline': 'Everyone offers services; Rafrafa offers solutions',
  'auth.lede': 'The integrated Arabic platform for running education, special education and counselling',
  'auth.foot': 'Every flutter carries the possibility of change',

  /* ── sign-in ──────────────────────────────────────────────────────── */
  'login.title': 'Sign in',
  'login.email': 'Email address',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.failed': 'Sign-in failed — please try again.',
  'login.selfServiceNote': 'Your platform account is created for you by your institution’s administration — there is no self-registration, except for specialists and counsellors:',
  'login.selfServiceLink': 'create your professional account',
  'login.guardianNote': 'Guardians start on their own — create your account, add your children, then ask their institutions to link them:',
  'login.guardianLink': 'create a guardian account',
  'login.lockoutNote': 'Five failed attempts lock the account temporarily, and every attempt is written to the audit log.',

  /* ── status pages ─────────────────────────────────────────────────── */
  'status.noContext.title': 'Your account has no working context yet',
  'status.noContext.text': 'Your account exists but is not linked to an institution, and holds neither a professional profile nor a link to a student. If you are waiting for an institutional invitation it will arrive by email; if you are a practitioner, complete your professional profile.',
  'status.noContext.action': 'Open my professional profile',
  'status.forbidden.title': 'This screen is outside your permissions at this institution',
  'status.forbidden.withPermission': 'It requires the “{permission}” permission at the currently active institution. If this is a mistake, speak to your institution’s administration — permissions are fixed by role and are never granted individually.',
  'status.forbidden.plain': 'If this is a mistake, speak to your institution’s administration.',
  'status.forbidden.action': 'Back to my start page',
  'status.notFound.title': 'Page not found',
  'status.notFound.aside': 'The link may be out of date, or the page may have been renamed.',
  'status.notFound.text': 'We could not find this path on the platform.',
  'status.notFound.action': 'Back to the home page',

  /* ── public directories ───────────────────────────────────────────── */
  'directory.title': 'Public directories',
  'directory.sub': 'Only those who chose to be listed — and no contact details for anyone',
  'directory.error': 'The directories could not be loaded right now — please try again shortly.',
  'directory.jobs.title': 'Vacancies',
  'directory.jobs.emptyTitle': 'No open vacancies at the moment',
  'directory.jobs.emptyText': 'When an institution advertises a professional role it appears here immediately.',
  'directory.jobs.applyHint': 'To apply: register a professional account, then apply from “Vacancies” in your account.',
  'directory.institutions.title': 'Institution directory',
  'directory.institutions.emptyTitle': 'No institutions listed yet',
  'directory.institutions.emptyText': 'Being listed here is each institution’s own choice.',
  'directory.specialists.title': 'Practitioner directory',
  'directory.specialists.emptyTitle': 'No practitioners listed yet',
  'directory.specialists.emptyText': 'Being listed here is each practitioner’s own choice, made from their professional profile.',
  'directory.specialists.years': '{n} years',
  'directory.specialists.verified': 'Verified',
  'directory.privacyNote': 'No email address, phone number or ID number appears in these directories. Contact goes through the platform: an invitation the account holder accepts.',
  'kind.SPECIALIST': 'Specialist',
  'kind.COUNSELOR': 'School counsellor',

  /* ── certificate verification ─────────────────────────────────────── */
  'verify.title': 'Verify a performance certificate',
  'verify.field': 'Certificate number',
  'verify.hint': 'Eight characters or more, exactly as printed on the certificate.',
  'verify.valid.title': 'Valid and in force',
  'verify.k.holder': 'Holder',
  'verify.k.institution': 'Institution',
  'verify.k.period': 'Period',
  'verify.k.issuedAt': 'Issued',
  'verify.note': 'The certificate shows the band (“Excellent” or “Good”), not the score and not the ranking — verification answers “is it genuine and in force?” and does not rank people in public.',
  'verify.invalid.title': 'Not valid',
  'verify.invalid.hint': 'Check the number exactly as printed on the certificate.',
  'band.EXCELLENT': 'Excellent',
  'band.GOOD': 'Good',

  /* ── public institution page ──────────────────────────────────────── */
  'pubInst.title': 'Institution page',
  'pubInst.license': 'Licence {n}',
  'pubInst.country': 'Jordan',
  'pubInst.tracks': 'Tracks offered',
  'pubInst.authorities': 'Supervising authorities',
  'pubInst.authorities.emptyTitle': 'No supervising authorities declared',
  'pubInst.authorities.emptyText': 'The institution has not declared its supervising authorities on its public page.',
  'pubInst.note': 'This page introduces the institution and shows no data about students or staff — that stays inside the platform, for those it concerns.',
  'authority.STATUTORY_MANDATORY': 'Mandatory authority',
  'authority.ELECTIVE_OPTIONAL': 'Elective authority',

  /* ── Public centre card ─────────────────────────────────────────────── */
  'pubInst.logoAlt': 'Logo of {name}',
  'pubInst.specialEd': 'Special-education staff on the team',
  'pubInst.alsoSchool': 'Also a school — morning academic',
  'pubInst.contact': 'Location and contact',
  'pubInst.address': 'Address',
  'pubInst.phone': 'Phone',
  'pubInst.email': 'Email',
  'pubInst.website': 'Website',
  'pubInst.map': 'Location on the map',
  'pubInst.mapOpen': 'Open the location in maps',
  'pubInst.mapNone': 'No map location published yet — ask the centre for directions.',
  'pubInst.contactNone': 'The centre has published no contact details.',
  'pubInst.more': 'About the institution',
  'pubInst.about': 'About',
  'pubInst.achievements': 'Achievements',
  'pubInst.moreNone': 'The centre has not written an introduction yet.',
  'pubInst.counts.students': 'Students',
  'pubInst.counts.staff': 'Staff',
  'pubInst.counts.note': 'Two aggregate numbers the centre chose to publish — no student or staff name ever appears on a public page.',
  'pubInst.gallery': 'Photo album',
  'pubInst.gallery.emptyTitle': 'No published photos',
  'pubInst.gallery.emptyText': 'The centre has not published its album — ask for a tour of the premises.',
  'pubInst.galleryAlt': 'Photo from {name}',

  /* ── Directory: logo and location ───────────────────────────────────── */
  'directory.institutions.mapOpen': 'Location on the map',


  /* ── 0027 — records & knowledge ── */
  'nav.app.requiredFields': 'Required fields',
  'nav.app.overview': 'Follow-up board',
  'nav.app.news': 'News',
  'nav.app.goalBank': 'Goal bank',
  'nav.platform.news': 'Platform news (published to everyone)',
  'nav.platform.goalBank': 'Platform goal bank',
  'nav.me.news': 'My institution news',

  /* ── Public news (/news) and its landing section ── */
  'news.public.title': 'News',
  'news.public.empty.title': 'No news published yet',
  'news.public.by': 'From the platform team',
  'news.kind.ANNOUNCEMENT': 'Announcement',
  'news.kind.ACHIEVEMENT': 'Achievement',
  'landing.nav.courses': 'Academy',
  'landing.nav.centers': 'Institutions',
  'landing.nav.specialists': 'Specialists',
  'landing.nav.jobs': 'Jobs',
  'landing.nav.cadres': 'Professionals',
  'landing.hero.join': 'Join your centre or school',
  'landing.doors.aria': 'The four doors',
  'landing.vision.t': 'Our vision',
  'landing.vision.d': 'That Rafrafa becomes the space where everyone in special education, teaching and counselling meets — from a child and their family to a centre, a school, a practitioner, a teacher, a trainer, a consultant and an independent specialist — in one system where each person can find what they need, offer what they have, grow in their role, and reach the others easily and clearly.',
  'landing.mission.t': 'Our mission',
  'landing.mission.d': 'That Rafrafa be an integrated digital system bringing the sector, its people and its institutions into one space; easing access to services, knowledge, expertise, training, consultations and opportunities; supporting student files and their journeys; and enabling specialists, institutions and families to connect, collaborate and grow — so that work in the sector becomes more connected and more organised, and every party is given the tools and the opportunities to play their role and deepen their impact.',
  'landing.purpose.aria': 'Rafrafa vision and mission',
  'public.backHome': 'Back to the home page',
  'landing.door.centers2.t': 'Organisations',
  'landing.door.centers2.d': 'When you are looking for a centre or a school for your child, you find them here — their services, their locations, and how to reach them.',
  'landing.door.centers2.meta': '{n} organisations listed',
  'landing.door.centers2.metaEmpty': 'The public directory of organisations',
  'landing.door.specialists.t': 'Specialists and consultants',
  'landing.door.specialists.d': 'Specialists and counsellors with accounts they own, verified credentials and declared specialties.',
  'landing.door.specialists.meta': '{n} listed specialists',
  'landing.door.specialists.metaEmpty': 'Public specialist directory',
  'landing.door.academy.t': 'Academy',
  'landing.door.academy.d': 'Training courses and accredited virtual academies — for specialists and parents, with verifiable certificates.',
  'landing.door.academy.meta': '{n} courses — {free} free',
  'landing.door.academy.metaEmpty': 'Courses and verified certificates',
  'landing.door.consult.t': 'Consultations',
  'landing.door.consult.d': 'For individuals in full privacy, and for children, adolescents and their families — accredited specialists; pick the specialty and book.',
  'landing.door.consult.meta': 'A documented remote session',
  'landing.nav.consult': 'Consultations',
  'landing.cta.signin': 'Sign in',
  'landing.door.consult.meta2': '{n} consultants with published rates',
  'landing.strip.consult.t': 'Book a consultation',
  'landing.strip.consult.d': 'Pick a consultant from their rate and CV, then pick your day and time from their calendar.',
  'landing.verify.title': 'Check before you trust',
  'landing.verify.lead': 'A certificate in hand, or a name claiming to be a professional on Rafrafa — the code tells the truth, with no sign-in and no middleman.',
  'landing.verify.cert.t': 'Course certificate',
  'landing.verify.cert.d': 'Enter the certificate code to see its holder, the course, the issuing body and its date.',
  'landing.verify.pro.t': 'Professional performance certificate',
  'landing.verify.pro.d': 'Enter the code to see its band and the issuing organisation — no score, no ranking.',
  'landing.why.title': 'Why Rafrafa?',
  'landing.why.lead': 'Because education and rehabilitation need integration, not scattered services.',
  'landing.why.more': 'And because the future of education needs more than digital tools: a system that connects everyone, saves time and effort, raises the quality of educational and rehabilitation services, and keeps every party developing.',
  'landing.start.title': 'Start here',
  'landing.start.lead': 'Choose who you are — the rest is a few short steps.',
  'landing.about.title': 'Who we are',
  'landing.about.lead': 'Rafrafa is an integrated digital system that runs education, special education and counselling — schools, centres, teachers, independent specialists and consultants — and connects everyone they serve in one secure space.',
  'landing.join.guardian': 'Register as a guardian',
  'landing.join.center': 'Join your centre or school to Rafrafa',
  'landing.centers.kicker': 'Listed',
  'landing.centers.title': 'Centres and schools on Rafrafa',
  'landing.centers.lead': 'Organisations running on the platform that chose to appear in its public directory — they run on Rafrafa, they are not its partners.',
  'landing.centers.all': 'All centres',
  'landing.centers.empty': 'No centre has chosen public listing yet — when one does it appears here with its name and city.',
  'landing.courses.kicker': 'Academy',
  'landing.courses.title': 'Latest courses',
  'landing.courses.lead': 'Courses published now — paid ones at their price, free ones as community service.',
  'landing.courses.all': 'Open the Academy',
  'landing.courses.empty': 'No courses published yet — the first one will appear here with its duration and certificate.',
  'landing.strip.jobs.t': 'Jobs',
  'landing.strip.jobs.d': 'Vacancies posted by centres and schools — read without an account, apply with your professional account.',
  'landing.strip.verify.t': 'Verify a certificate or academy',
  'landing.strip.verify.d': 'Enter the code and get the answer — no account, no sign-in.',
  'landing.contact.kicker': 'Contact',
  'landing.contact.title': 'Get in touch',
  'landing.contact.lead': 'Questions from centres, partnerships and support — and our social accounts.',
  'landing.contact.socialAria': 'Rafrafa social accounts',
  'landing.partners.kicker': 'Making an impact together',
  'landing.partners.title': 'Success partners',
  'landing.partners.lede': 'The organisations and programmes that have backed Rafrafa since its first idea.',
  'landing.news.kicker': 'From the platform team',
  'landing.news.title': 'Latest news',
  'landing.news.all': 'All news',


  /* ── 0031 — clients, paid consultations and wallet ── */
  'nav.me.services.browse': 'Book a consultation',
  'nav.me.consultations': 'My consultations',
  'nav.me.myServices': 'My services',
  'nav.me.availability': 'My calendar',
  'nav.me.notifications': 'Notifications',
  'nav.me.studentMessages': 'Student messages',
  'nav.app.messages': 'Messages',
  'nav.app.forms': 'Digital forms',
  'nav.app.attachments': 'Upload attachment',
  'nav.me.wallet': 'My wallet',
  'nav.platform.payouts': 'Payout requests',
  'nav.platform.finance': 'Finance',
  'nav.platform.complaints': 'Consultation complaints',
  'nav.platform.pricing': 'Platform pricing',
  'nav.platform.courseProviders': 'Course provider applications',
  'nav.platform.consultants': 'Consultation activation requests',
  'nav.platform.academies': 'Academy review',
  'nav.platform.institutionRequests': 'Centre registration requests',
  'nav.me.courseProvider': 'Academic registration',
  'nav.me.academy': 'My academy',
  'nav.me.academyManage': 'Academy management',
  'nav.me.courses': 'My courses',
  'nav.me.browseCourses': 'Academy',
  'nav.me.enrollments': 'My enrolments',
  'login.clientNote': 'Beneficiaries sign in with their mobile number:',
  'login.clientLink': 'Client sign-in',

  /* ── Cost calculator (public page) ─────────────────────────────────── */
  'calc.kicker': 'Rough estimate',
  'calc.title': 'Estimate your cost',
  'calc.lead': 'Estimate your monthly and yearly subscription by organisation type and number of students — the maths runs in your browser and nothing leaves it.',
  'calc.mode': 'Subscription type',
  'calc.mode.center': 'Special-education centre',
  'calc.mode.school': 'School supervision',
  'calc.mode.pro': 'Independent professional',
  'calc.students': 'Number of students',
  'calc.studentsHint': 'The number of live seats you need.',
  'calc.terms': 'Number of terms',
  'calc.termsHint': 'Two or three terms per year.',
  'calc.firstTerm': 'First-term discount',
  'calc.firstTermHint': 'A reduced price for the supervision seat in the first term.',
  'calc.monthly': 'Monthly',
  'calc.perMonth': 'per month',
  'calc.yearly': 'Yearly',
  'calc.perYear': 'per year',
  'calc.perStudent': 'Per student',
  'calc.zero': 'Add students',
  'calc.breakdown': 'How it is calculated',
  'calc.line.license': 'Institution licence: {amount} per year.',
  'calc.line.seat': 'Student seats: {amount} per month × {n} students × 12 months.',
  'calc.line.supervision': 'Supervision seats: {amount} per term × {n} students × {terms} terms.',
  'calc.line.supervisionFirst': 'First term (reduced): {amount} × {n} students.',
  'calc.line.pro': 'Independent professional subscription: {amount} per month.',
  'calc.currency': 'All amounts are in US dollars.',
  'calc.disclaimer': 'This is an indicative estimate, not a binding quote; the final invoice is issued by the platform based on your plan.',

  /* ── 18 Sept 2026 — Account settings ─────────────────────────────────── */
  "hero.hello": "Hello,",
  "hero.aria": "My card",
  "hero.changePhoto": "Change photo",
  "hero.photoSaved": "Photo saved.",
  "hero.photoFailed": "Could not upload the photo.",
  "hero.removePhoto": "Remove photo",
  "hero.photoRemoved": "Photo removed.",
  "account.photo": "Account photo",
  "account.photoHint": "PNG, JPEG or WebP up to 5 MB — shown on your page and to your colleagues.",
  "account.emailChange": "Change sign-in email",
  "account.emailNew": "New email",
  "account.emailSend": "Send a code to the new email",
  "account.emailSent": "A code was sent to the new email — enter it here.",
  "account.emailConfirm": "Confirm new email",
  "account.emailChanged": "This is now your sign-in email. Sign in again with it.",
  "account.emailChangeNote": "A code goes to the new email; once confirmed it becomes your sign-in email and current sessions end.",
  "auth.forgot": "Forgot your password?",
  "forgot.title": "Reset password",
  "forgot.lede": "Enter your registered email and a six-digit code will be sent to it.",
  "forgot.send": "Send code",
  "forgot.sent": "If the email is registered, a code valid for ten minutes was sent.",
  "forgot.code": "Code",
  "forgot.newPw": "New password",
  "forgot.reset": "Set password",
  "forgot.done": "Your password changed — sign in with it.",
  "forgot.back": "Back to sign in",
  'nav.me.account': 'Account settings',
  'account.title': 'Account settings',
  'account.subtitle': 'Your photo, name, mobile number, email and password.',
  'account.profile': 'My details',
  'account.password': 'Password',
  'account.fullName': 'Full name',
  'account.phone': 'Mobile number',
  'account.phoneHint': 'Digits only, optionally prefixed with "+". Changing the number resets its verification until you confirm the new one.',
  'account.phoneVerifiedHint': 'Verified number.',
  'account.phoneInvalid': 'Invalid mobile number — 9 to 15 digits.',
  'account.nameShort': 'The name is too short.',
  'verify.email.title': 'Verify your email',
  'verify.phone.title': 'Verify your phone number',
  'verify.send': 'Send the verification code',
  'verify.sent': 'Code sent — check your inbox.',
  'verify.checkInbox': 'We sent a six-digit code to {target}. Check your inbox and your spam folder.',
  'verify.code': 'Verification code',
  'verify.confirm': 'Confirm',
  'verify.resend': 'Resend',
  'verify.needSix': 'The code is six digits.',
  'verify.done': 'Channel verified.',
  'verify.failed': 'Could not complete verification.',
  'account.emailVerified': 'Email verified',
  'account.emailFixed': 'Your email is your sign-in identity and the key to invitations and links, so it cannot be changed here. Contact platform administration to change it.',
  'account.yes': 'Yes',
  'account.no': 'No',
  'account.save': 'Save my details',
  'account.saved': 'Your details were saved.',
  'account.saveFailed': 'Could not save your details.',
  'account.noChange': 'Nothing to save.',
  'account.pwNote': 'Changing your password ends all your open sessions — including this one — and you will sign in again with the new password.',
  'account.pwCurrent': 'Current password',
  'account.pwNew': 'New password',
  'account.pwConfirm': 'Confirm new password',
  'account.pwPolicy': 'At least 10 characters, with a letter and a digit.',
  'account.pwMismatch': 'The confirmation does not match the new password.',
  'account.pwFillAll': 'Complete all three fields to enable the button.',
  'account.pwSave': 'Change password',
  'account.pwChanged': 'Password changed — please sign in again.',
  'account.pwFailed': 'Could not change the password.',

  /* ── Platform administration accounts ────────────────────────────────── */
  'nav.platform.contact': 'Contact messages',
  'nav.platform.credentials': 'Staff certificates',
  'nav.platform.callTest': 'Digital calling',
  'nav.platform.partners': 'Success partners',
  'nav.platform.health': 'System health',
  'nav.platform.forms': 'Form templates',
  'nav.app.attendance': 'Daily attendance',
  'nav.platform.contentReports': 'Content reports',
  'nav.platform.growth': 'Platform growth',
  'nav.platform.deletions': 'Account deletion requests',
  'nav.platform.sandbox': 'Sandbox accounts',
  'nav.platform.accounts': 'Accounts',
  'nav.platform.admins': 'Admin accounts',
  'padmin.title': 'Platform administration accounts',
  'padmin.subtitle': 'Platform administration and its departments (beneficiary services · academies · centers and schools · finance) on standalone accounts — an email that holds no other role on the platform. Each department sees only its own work.',
  'padmin.create': 'Create admin account',
  'padmin.email': 'Email',
  'padmin.fullName': 'Name',
  'padmin.password': 'Initial password (optional)',
  'padmin.passwordHint': 'Leave empty to generate a temporary password shown once. Either way it must be changed on first sign-in.',
  'padmin.created': 'Account created.',
  'padmin.regranted': 'Administrator role restored to an existing account; its password was not changed.',
  'padmin.tempTitle': 'Temporary password — shown once',
  'padmin.tempBody': 'Hand it to the account holder securely. It will not be shown again, and they must change it on first sign-in.',
  'padmin.mustChange': 'Awaiting first sign-in',
  'padmin.lastLogin': 'Last sign-in',
  'padmin.never': 'Never signed in',
  'padmin.empty': 'No accounts — which cannot happen: you are here.',
  'padmin.fillRequired': 'Enter the email and name to enable the button.',

  /* ── Schedule ─────────────────────────────────────────────────────────── */
  'nav.app.schedule': 'My calendar',
  'nav.app.scheduleAll': 'Institution calendar',
  'nav.me.calendar': 'My calendar',
  'nav.app.staffAvailability': 'My working hours',
  'nav.app.homeTraining': 'Home training',
  'nav.app.cases': 'Cases dashboard',
  'nav.app.transport': 'School bus',
  'nav.app.promotion': 'End-of-year promotion',
  'nav.app.guardianInvitations': 'Guardian invitations',
  'nav.app.staffAvailabilityAll': 'Staff working hours',
  'schedule.title': 'Schedule',
  'schedule.mine': 'My schedule',
  'schedule.subtitle': 'Special-education and counselling sessions scheduled this week — Amman time.',
  'schedule.staff': 'Staff member',
  'schedule.me': 'Me',
  'schedule.today': 'Today',
  'schedule.prevWeek': 'Previous week',
  'schedule.nextWeek': 'Next week',
  'schedule.week': 'Week',
  'schedule.day': 'Day',
  'schedule.empty': 'No appointments this week.',
  'schedule.emptyHint': 'Schedule a new session from the Sessions page or the button above.',
  'schedule.new': 'Schedule a session',
  'schedule.minutes': 'min',
  'schedule.count': '{n} appointments',
  'schedule.print': 'Print week',

  /* ── Sign-up: choose account type ────────────────────────────────────── */
  'register.title': 'Create an account',
  'register.lead': 'Choose the account type first — the rest is a single form.',
  'register.guardian': 'Guardian',
  'register.guardian.d': 'Add your child and request a link to their centre.',
  'register.professional': 'Professional account',
  'register.professional.d': 'Specialist · counsellor · consultant · trainer',
  'register.center': 'Centre or school',
  'register.center.d': 'Your onboarding request reaches platform administration.',
  'register.haveAccount': 'I already have an account',
  'login.registerNote': 'No account yet?',
  'login.registerLink': 'Create an account',
  'login.beneficiaryNote': 'Beneficiaries sign in with their mobile number:',
  'login.beneficiaryLink': 'Beneficiary sign-in',

  /* ── Guardian messaging ───────────────────────────────────────────────── */
  'gmsg.title': 'Messages',
  'gmsg.guardianTitle': "My messages with {name}'s staff",
  'gmsg.staffTitle': 'Messages with the guardian',
  'gmsg.to': 'To',
  'gmsg.body': 'Message',
  'gmsg.send': 'Send',
  'gmsg.sent': 'Message sent.',
  'gmsg.empty': 'No messages yet.',
  'gmsg.emptyGuardian': "Write to your child's specialist or teacher — they get a notification and reply here.",
  'gmsg.emptyStaff': 'Write to the guardian — they get a notification and reply here.',
  'gmsg.noContacts': 'No staff assigned to this child at their institution yet — assigned staff appear here.',
  'gmsg.noGuardians': 'No activated guardian for this student at your institution.',
  'gmsg.unread': 'Unread',
  'gmsg.markRead': 'Marked as read',
  'gmsg.institution': 'Institution',
  'gmsg.limit': 'Up to 2,000 characters.',

  /* ── Printing ─────────────────────────────────────────────────────────── */
  'print.action': 'Print',
  'print.printedAt': 'Printed on',
  'print.by': 'by',

  /* ── Theme toggle (18 Sept 2026) ─────────────────────────────────────────── */
  'theme.toLight': 'Light mode',
  'theme.toDark': 'Dark mode',

};
